import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@workspace/ui/components/alert';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { Checkbox } from '@workspace/ui/components/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@workspace/ui/components/drawer';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import { toast } from '@workspace/ui/components/sonner';
import { useIsMobile } from '@workspace/ui/hooks/use-mobile';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  Copy,
  Plus,
  Terminal,
  Trash,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useSessionScopes } from '~/hooks/use-session-scopes';
import { m } from '~/paraglide/messages.js';
import {
  filterPresets,
  filterScopeGroups,
  SCOPE_GROUPS,
  type ScopeGroup,
  USE_CASE_PRESETS,
  type UseCasePreset,
} from '../-config/scope-catalog';
import { useCreateApiKey } from '../-hooks/use-api-keys';

type WizardStep = 'use-case' | 'scopes' | 'config' | 'success';

interface MetadataEntry {
  key: string;
  value: string;
}

function StepIndicator({ current }: { current: WizardStep }) {
  const steps: { id: WizardStep; label: string }[] = [
    { id: 'use-case', label: m['developer.api_keys.wizard.step_use_case']() },
    { id: 'scopes', label: m['developer.api_keys.wizard.step_scopes']() },
    { id: 'config', label: m['developer.api_keys.wizard.step_config']() },
  ];

  const currentIdx = steps.findIndex((s) => s.id === current);

  return (
    <nav
      className="flex items-center gap-1.5 px-1"
      aria-label="Wizard progress"
    >
      {steps.map((step, i) => {
        const isActive = step.id === current;
        const isCompleted = currentIdx > i;
        const isPast = current === 'success';

        return (
          <div key={step.id} className="flex items-center gap-1.5">
            {i > 0 && (
              <div
                className={`h-px w-6 transition-colors duration-200 ${
                  isCompleted || isPast ? 'bg-primary' : 'bg-border'
                }`}
              />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-all duration-200 ${
                  isPast || isCompleted
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                      ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {isCompleted || isPast ? <Check className="h-3 w-3" /> : i + 1}
              </div>
              <span
                className={`text-xs font-medium transition-colors duration-200 ${
                  isActive ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </nav>
  );
}

function PresetButton({
  preset,
  isSelected,
  onToggle,
}: {
  preset: UseCasePreset;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const Icon = preset.icon;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`group flex items-start gap-3 rounded-lg border p-3.5 text-left transition-all duration-150 ${
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
          : 'border-border hover:border-primary/40 hover:bg-muted/50'
      }`}
      aria-pressed={isSelected}
    >
      <div
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors ${
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-foreground">
            {preset.label}
          </span>
          {preset.scopes.length > 0 && (
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {preset.scopes.length} scopes
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
          {preset.description}
        </p>
      </div>
    </button>
  );
}

function UseCaseStep({
  selectedPresets,
  onToggle,
  presets,
}: {
  selectedPresets: Set<string>;
  onToggle: (id: string) => void;
  presets: UseCasePreset[];
}) {
  const nonCustom = presets.filter((p) => p.id !== 'custom');
  const custom = presets.find((p) => p.id === 'custom');

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {m['developer.api_keys.wizard.use_case_description']()}
      </p>
      <div className="grid gap-2">
        {nonCustom.map((preset) => (
          <PresetButton
            key={preset.id}
            preset={preset}
            isSelected={selectedPresets.has(preset.id)}
            onToggle={() => onToggle(preset.id)}
          />
        ))}
      </div>
      {custom && (
        <PresetButton
          preset={custom}
          isSelected={selectedPresets.has(custom.id)}
          onToggle={() => onToggle(custom.id)}
        />
      )}
    </div>
  );
}

function ScopeGroupSection({
  group,
  selectedScopes,
  onToggle,
}: {
  group: ScopeGroup;
  selectedScopes: Set<string>;
  onToggle: (scope: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const Icon = group.icon;
  const count = group.scopes.filter((s) => selectedScopes.has(s.key)).length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-muted/50">
        <div className="flex items-center gap-2.5">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-foreground">{group.label}</span>
          {count > 0 && (
            <Badge
              variant="default"
              className="h-5 min-w-5 justify-center px-1.5 text-[10px]"
            >
              {count}
            </Badge>
          )}
        </div>
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
            open ? 'rotate-90' : ''
          }`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-1 pb-2 pl-9 pr-3">
          {group.scopes.map((scope) => (
            <label
              key={scope.key}
              htmlFor={`scope-${scope.key}`}
              className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/50"
            >
              <Checkbox
                id={`scope-${scope.key}`}
                checked={selectedScopes.has(scope.key)}
                onCheckedChange={() => onToggle(scope.key)}
                className="mt-0.5"
                aria-label={scope.label}
              />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground">{scope.label}</span>
                  <code className="font-mono text-[11px] text-muted-foreground">
                    {scope.key}
                  </code>
                </div>
                <p className="text-xs text-muted-foreground">
                  {scope.description}
                </p>
              </div>
            </label>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ScopesStep({
  selectedScopes,
  onToggle,
  scopeGroups,
}: {
  selectedScopes: Set<string>;
  onToggle: (scope: string) => void;
  scopeGroups: ScopeGroup[];
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {m['developer.api_keys.wizard.scopes_description']()}
        </p>
        <Badge variant="outline" className="shrink-0">
          {selectedScopes.size} selected
        </Badge>
      </div>
      {selectedScopes.size === 0 && (
        <p className="text-sm font-medium text-destructive" role="alert">
          {m['developer.api_keys.wizard.scopes_empty_error']()}
        </p>
      )}
      <div className="overflow-hidden rounded-lg border border-border">
        {scopeGroups.map((group, i) => (
          <div key={group.id} className={i > 0 ? 'border-t border-border' : ''}>
            <ScopeGroupSection
              group={group}
              selectedScopes={selectedScopes}
              onToggle={onToggle}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfigStep({
  name,
  onNameChange,
  nameError,
  domains,
  onAddDomain,
  onRemoveDomain,
  expiration,
  onExpirationChange,
  metadata,
  onAddMetadata,
  onRemoveMetadata,
}: {
  name: string;
  onNameChange: (v: string) => void;
  nameError: string | null;
  domains: string[];
  onAddDomain: (d: string) => void;
  onRemoveDomain: (i: number) => void;
  expiration: string;
  onExpirationChange: (v: string) => void;
  metadata: MetadataEntry[];
  onAddMetadata: (entry: MetadataEntry) => void;
  onRemoveMetadata: (i: number) => void;
}) {
  const [newDomain, setNewDomain] = useState('');
  const [metaKey, setMetaKey] = useState('');
  const [metaVal, setMetaVal] = useState('');

  const addDomain = () => {
    const trimmed = newDomain.trim();
    if (trimmed) {
      onAddDomain(trimmed);
      setNewDomain('');
    }
  };

  const addMeta = () => {
    if (metaKey && metaVal) {
      onAddMetadata({ key: metaKey, value: metaVal });
      setMetaKey('');
      setMetaVal('');
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="key-name">
          {m['developer.api_keys.create_drawer.name_label']()}
        </Label>
        <Input
          id="key-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={m['developer.api_keys.create_drawer.name_placeholder']()}
          aria-invalid={!!nameError}
        />
        {nameError && (
          <p className="text-sm text-destructive" role="alert">
            {nameError}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>{m['developer.api_keys.create_drawer.domains_label']()}</Label>
        <p className="text-xs text-muted-foreground">
          {m['developer.api_keys.create_drawer.domains_description']()}
        </p>
        {domains.length > 0 && (
          <div className="space-y-1.5">
            {domains.map((domain, i) => (
              <div
                key={`${domain}-${i}`}
                className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5"
              >
                <span className="flex-1 truncate text-sm">{domain}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveDomain(i)}
                  className="h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input
            type="url"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addDomain();
              }
            }}
            placeholder={m[
              'developer.api_keys.create_drawer.domain_placeholder'
            ]()}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={addDomain}
            disabled={!newDomain.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="key-expiration">
          {m['developer.api_keys.create_drawer.expiration_label']()}
        </Label>
        <Input
          id="key-expiration"
          type="date"
          value={expiration}
          onChange={(e) => onExpirationChange(e.target.value)}
          style={{ pointerEvents: 'auto' }}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
        />
        <p className="text-xs text-muted-foreground">
          {m['developer.api_keys.create_drawer.expiration_description']()}
        </p>
      </div>

      <div className="space-y-2">
        <Label>{m['developer.api_keys.create_drawer.metadata_label']()}</Label>
        <p className="text-xs text-muted-foreground">
          {m['developer.api_keys.create_drawer.metadata_description']()}
        </p>
        {metadata.length > 0 && (
          <div className="space-y-1.5">
            {metadata.map((entry, i) => (
              <div
                key={`${entry.key}-${i}`}
                className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5"
              >
                <span className="flex-1 truncate text-sm">
                  <span className="font-medium">{entry.key}</span>
                  <span className="text-muted-foreground">
                    {' '}
                    = {entry.value}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveMetadata(i)}
                  className="h-6 w-6 p-0"
                >
                  <Trash className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Input
            placeholder={m[
              'developer.api_keys.create_drawer.metadata_key_placeholder'
            ]()}
            value={metaKey}
            onChange={(e) => setMetaKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addMeta();
              }
            }}
          />
          <Input
            placeholder={m[
              'developer.api_keys.create_drawer.metadata_value_placeholder'
            ]()}
            value={metaVal}
            onChange={(e) => setMetaVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addMeta();
              }
            }}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addMeta}
          disabled={!metaKey || !metaVal}
          className="w-full"
        >
          <Plus className="mr-2 h-3 w-3" />
          {m['developer.api_keys.create_drawer.add_metadata']()}
        </Button>
      </div>
    </div>
  );
}

function SuccessPanel({
  secretKey,
  publishableKey,
  onClose,
}: {
  secretKey: string;
  publishableKey: string;
  onClose: () => void;
}) {
  const copyValue = useCallback(async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(m['common.copied_to_clipboard']());
    } catch {
      toast.error(`Failed to copy ${label}`);
    }
  }, []);

  const cliCommand = `bloque login --api-key ${secretKey}`;

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <Alert
        variant="destructive"
        className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-900/20"
      >
        <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
        <AlertTitle className="text-yellow-800 dark:text-yellow-400">
          {m['developer.api_keys.wizard.success_title']()}
        </AlertTitle>
        <AlertDescription className="text-yellow-700 dark:text-yellow-300">
          {m['developer.api_keys.wizard.success_warning']()}
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            {m['developer.api_keys.wizard.secret_key_label']()}
          </Label>
          <p className="text-[11px] text-muted-foreground">
            {m['developer.api_keys.wizard.secret_key_hint']()}
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 overflow-hidden rounded-md border bg-muted p-2.5 font-mono text-xs break-all">
              {secretKey}
            </div>
            <Button
              size="icon"
              variant="outline"
              onClick={() => copyValue(secretKey, 'secret key')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {publishableKey && (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              {m['developer.api_keys.wizard.publishable_key_label']()}
            </Label>
            <p className="text-[11px] text-muted-foreground">
              {m['developer.api_keys.wizard.publishable_key_hint']()}
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 overflow-hidden rounded-md border bg-muted p-2.5 font-mono text-xs break-all">
                {publishableKey}
              </div>
              <Button
                size="icon"
                variant="outline"
                onClick={() => copyValue(publishableKey, 'publishable key')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            {m['developer.api_keys.wizard.cli_command_label']()}
          </Label>
          <p className="text-[11px] text-muted-foreground">
            {m['developer.api_keys.wizard.cli_command_hint']()}
          </p>
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 overflow-hidden rounded-md border bg-zinc-950 p-2.5 font-mono text-xs text-green-400">
              <Terminal className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
              <span className="break-all">{cliCommand}</span>
            </div>
            <Button
              size="icon"
              variant="outline"
              onClick={() => copyValue(cliCommand, 'CLI command')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1" />

      <Button onClick={onClose} className="w-full">
        {m['developer.api_keys.wizard.done_button']()}
      </Button>
    </div>
  );
}

function ScopeSummaryBadges({ scopes }: { scopes: Set<string> }) {
  if (scopes.size === 0) return null;
  const grouped = new Map<string, number>();
  for (const key of scopes) {
    for (const group of SCOPE_GROUPS) {
      if (group.scopes.some((s) => s.key === key)) {
        grouped.set(group.label, (grouped.get(group.label) || 0) + 1);
        break;
      }
    }
  }
  return (
    <div className="flex flex-wrap gap-1">
      {[...grouped.entries()].map(([label, count]) => (
        <Badge key={label} variant="secondary" className="text-[10px]">
          {label} ({count})
        </Badge>
      ))}
    </div>
  );
}

export function CreateApiKeyDrawer() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>('use-case');
  const [selectedPresets, setSelectedPresets] = useState<Set<string>>(
    new Set(),
  );
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
  const [keyName, setKeyName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [expiration, setExpiration] = useState('');
  const [metadata, setMetadata] = useState<MetadataEntry[]>([]);
  const [createdKey, setCreatedKey] = useState<{
    secret_key: string;
    publishable_key: string;
  } | null>(null);

  const createMutation = useCreateApiKey();
  const isMobile = useIsMobile();
  const sessionScopes = useSessionScopes();
  const availableGroups = useMemo(
    () => filterScopeGroups(SCOPE_GROUPS, sessionScopes),
    [sessionScopes],
  );
  const availablePresets = useMemo(
    () => filterPresets(USE_CASE_PRESETS, sessionScopes),
    [sessionScopes],
  );

  const resetWizard = useCallback(() => {
    setStep('use-case');
    setSelectedPresets(new Set());
    setSelectedScopes(new Set());
    setKeyName('');
    setNameError(null);
    setDomains([]);
    setExpiration('');
    setMetadata([]);
    setCreatedKey(null);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(resetWizard, 300);
  }, [resetWizard]);

  const handleTogglePreset = useCallback(
    (presetId: string) => {
      setSelectedPresets((prev) => {
        const next = new Set(prev);
        if (next.has(presetId)) {
          next.delete(presetId);
        } else {
          next.add(presetId);
        }
        const merged = new Set<string>();
        for (const id of next) {
          const preset = availablePresets.find((p) => p.id === id);
          if (preset) {
            for (const s of preset.scopes) merged.add(s);
          }
        }
        setSelectedScopes(merged);
        return next;
      });
    },
    [availablePresets],
  );

  const handleToggleScope = useCallback((scope: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!keyName.trim()) {
      setNameError(
        m['developer.api_keys.create_drawer.validation.name_required'](),
      );
      return;
    }
    if (domains.length === 0) {
      toast.error(
        m['developer.api_keys.create_drawer.validation.domain_required'](),
      );
      return;
    }

    let expirationValue: string;
    if (expiration) {
      const date = new Date(expiration);
      expirationValue = Math.floor(date.getTime() / 1000).toString();
    } else {
      expirationValue = 'never';
    }

    const metadataObject = metadata.reduce(
      (acc, { key, value }) => {
        if (key && value) acc[key] = value;
        return acc;
      },
      {} as Record<string, string>,
    );

    try {
      const result = await createMutation.mutateAsync({
        name: keyName.trim(),
        scopes: [...selectedScopes],
        domains,
        expiration: expirationValue,
        metadata:
          Object.keys(metadataObject).length > 0 ? metadataObject : undefined,
      });
      setCreatedKey({
        secret_key: result.secret_key,
        publishable_key: result.publishable_key,
      });
      setStep('success');
    } catch {
      // error handled by mutation hook
    }
  }, [keyName, domains, expiration, metadata, selectedScopes, createMutation]);

  const canProceedFromUseCase = selectedPresets.size > 0;
  const canProceedFromScopes = selectedScopes.size > 0;

  return (
    <Drawer
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
        else setOpen(true);
      }}
      direction={isMobile ? 'bottom' : 'right'}
    >
      <DrawerTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          {m['developer.api_keys.create_drawer.title']()}
        </Button>
      </DrawerTrigger>
      <DrawerContent
        className={
          isMobile
            ? 'mx-auto flex max-h-[90vh] max-w-2xl flex-col'
            : '!w-[44%] !max-w-none'
        }
      >
        {step === 'success' && createdKey ? (
          <>
            <DrawerHeader className="flex-shrink-0">
              <StepIndicator current="success" />
              <DrawerTitle className="mt-2">
                {m['developer.api_keys.wizard.success_heading']()}
              </DrawerTitle>
              <DrawerDescription>
                {m['developer.api_keys.wizard.success_subheading']()}
              </DrawerDescription>
            </DrawerHeader>
            <SuccessPanel
              secretKey={createdKey.secret_key}
              publishableKey={createdKey.publishable_key}
              onClose={handleClose}
            />
          </>
        ) : (
          <>
            <DrawerHeader className="flex-shrink-0 space-y-3">
              <StepIndicator current={step} />
              <div>
                <DrawerTitle>
                  {m['developer.api_keys.create_drawer.title']()}
                </DrawerTitle>
                <DrawerDescription>
                  {m['developer.api_keys.create_drawer.subtitle']()}
                </DrawerDescription>
              </div>
              {step !== 'use-case' && selectedScopes.size > 0 && (
                <ScopeSummaryBadges scopes={selectedScopes} />
              )}
            </DrawerHeader>

            <div
              className={`flex-1 overflow-y-auto px-4 ${
                isMobile ? 'min-h-0' : ''
              }`}
            >
              <div className="transition-opacity duration-150" key={step}>
                {step === 'use-case' && (
                  <UseCaseStep
                    selectedPresets={selectedPresets}
                    onToggle={handleTogglePreset}
                    presets={availablePresets}
                  />
                )}
                {step === 'scopes' && (
                  <ScopesStep
                    selectedScopes={selectedScopes}
                    onToggle={handleToggleScope}
                    scopeGroups={availableGroups}
                  />
                )}
                {step === 'config' && (
                  <ConfigStep
                    name={keyName}
                    onNameChange={(v) => {
                      setKeyName(v);
                      if (v.trim()) setNameError(null);
                    }}
                    nameError={nameError}
                    domains={domains}
                    onAddDomain={(d) => setDomains((prev) => [...prev, d])}
                    onRemoveDomain={(i) =>
                      setDomains((prev) => prev.filter((_, idx) => idx !== i))
                    }
                    expiration={expiration}
                    onExpirationChange={setExpiration}
                    metadata={metadata}
                    onAddMetadata={(entry) =>
                      setMetadata((prev) => [...prev, entry])
                    }
                    onRemoveMetadata={(i) =>
                      setMetadata((prev) => prev.filter((_, idx) => idx !== i))
                    }
                  />
                )}
              </div>
            </div>

            <DrawerFooter className="flex-shrink-0 border-t px-4 py-4">
              <div className="flex gap-2">
                {step !== 'use-case' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (step === 'scopes') setStep('use-case');
                      else if (step === 'config') setStep('scopes');
                    }}
                    className="gap-1.5"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {m['common.back']()}
                  </Button>
                )}
                <div className="flex-1" />
                {step === 'use-case' && (
                  <Button
                    onClick={() => setStep('scopes')}
                    disabled={!canProceedFromUseCase}
                    className="min-w-[120px]"
                  >
                    {m['developer.api_keys.wizard.continue']()}
                  </Button>
                )}
                {step === 'scopes' && (
                  <Button
                    onClick={() => setStep('config')}
                    disabled={!canProceedFromScopes}
                    className="min-w-[120px]"
                  >
                    {m['developer.api_keys.wizard.continue']()}
                  </Button>
                )}
                {step === 'config' && (
                  <Button
                    onClick={handleSubmit}
                    disabled={createMutation.isPending}
                    className="min-w-[140px]"
                  >
                    {createMutation.isPending
                      ? m['developer.api_keys.create_drawer.creating_button']()
                      : m['developer.api_keys.wizard.create_key']()}
                  </Button>
                )}
              </div>
              <DrawerClose asChild>
                <Button variant="ghost" type="button" className="w-full">
                  {m['common.cancel']()}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
